import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import L, { Map } from 'leaflet';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import { PieChart } from 'react-minimal-pie-chart';
import { formatNumber0 } from '../utilities/format';

interface Properties {
    superclusterRef: { [key: string]: any; } | undefined;
    options: any;
    data: any;
}

const PIE_CHART_ZOOM_THRESHOLD = 12;

const iconCountBadge = (item: any) => {
    const totalCount = item.entries.reduce(
        (sum: number, entry: any) => sum + (entry.assetCount ?? 1),
        0
    );
    const size = 44;
    const fontSize = totalCount >= 100 ? 10 : totalCount >= 10 ? 12 : 14;
    return L.divIcon({
        className: 'marker-count-badge',
        html: `<div style="
            width: ${size}px; height: ${size}px;
            border-radius: 50%;
            background: rgba(33, 150, 243, 0.9);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${fontSize}px;
            font-weight: 600;
            font-family: Lato, sans-serif;
            border: 2px solid white;
            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">${formatNumber0(totalCount)}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

const iconPieChart = (item: any, options: any, mapContext: Map) => {
    // Add null check for options
    if (!options) {
        return L.divIcon({
            className: 'marker-pie-chart',
            html: '<div></div>'
        });
    }

    const getSizePieChart = () => {
        if (mapContext.getZoom() <= 10) {
            return 50;
        } else if (mapContext.getZoom() <= 18) {
            return 60;
        } else {
            return 80;
        }
    }

    const getDataPieChart = () => {
        if (!options.colorMapping) {
            return [];
        }
        
        return options.colorMapping.map((mapping: any) => {
            if (options.fieldName) {
                return {
                    value: item.entries.filter((entry: any) => entry[options.fieldName] === mapping.value).length,
                    color: mapping.color,
                }
            } else {
                return {
                    value: item.entries.reduce((acc: number, curr: any) => {
                        return acc + (curr[mapping.value] ?? 0);
                    }, 0),
                    color: mapping.color,
                }
            }
        });
    }

    return L.divIcon({
        className: 'marker-pie-chart',
        html: ReactDOMServer.renderToString(
            <div style={{
                width: (getSizePieChart() + 8) * 2,
                height: (getSizePieChart() + 8) * 2,
                transform: 'translate(-50%, -50%)'
            }}>
                <PieChart
                    data={getDataPieChart()}
                    radius={getSizePieChart()}
                    center={[getSizePieChart() + 8, getSizePieChart() + 8]}
                    viewBoxSize={[(getSizePieChart() + 8) * 2, (getSizePieChart() + 8) * 2]}
                    lineWidth={70}
                    // TODO: Allow to specify format in options
                    label={({ dataEntry }) => `${formatNumber0(dataEntry.value)}${options.suffix || ''}`}
                    labelPosition={100 - 70 / 2}
                    labelStyle={(_index) => ({
                        fill: 'white',
                        fontSize: '12px',
                        fontFamily: 'Lato',
                    })}
                />
            </div>
        )
    })
}

const clusterByLatLon = (points: any) => {
    // TODO Move to cluster

    return Object.values(points.reduce((map: any, point: any) => {
        const key = `${point.lat}-${point.lon}`;

        map[key] = map[key] || { lat: point.lat, lon: point.lon, entries: [] };

        map[key].entries.push(point);

        return map;
    }, {}));
}

const RenderMarkerSuperCluster = (properties: Properties) => {
    const mapContext = useMap();
    const [groups, _setGroups] = useState(clusterByLatLon(properties.data));
    const [currentZoom, setCurrentZoom] = useState(() => mapContext.getZoom());

    useMapEvents({
        zoomend: () => setCurrentZoom(mapContext.getZoom()),
    });

    // Comprehensive defensive guards after all hooks
    if (!properties) {
        console.error("[RenderMarkerSuperCluster] properties is null or undefined");
        return <div>Loading...</div>;
    }
    
    if (typeof properties !== "object") {
        console.error("[RenderMarkerSuperCluster] properties is not an object:", typeof properties, properties);
        return <div>Loading...</div>;
    }
    
    // Ensure properties.options exists and is an object
    if (!properties.options) {
        console.warn("[RenderMarkerSuperCluster] properties.options is null/undefined, using empty object");
        properties.options = {};
    }
    
    if (typeof properties.options !== "object") {
        console.warn("[RenderMarkerSuperCluster] properties.options is not an object, using empty object");
        properties.options = {};
    }

    const clusterPieChart = properties.options?.clusterPieChart;
    const zoomThreshold = clusterPieChart?.zoomThreshold ?? PIE_CHART_ZOOM_THRESHOLD;
    const isZoomedOut = currentZoom < zoomThreshold;
    const showCountWhenZoomedOut =
        clusterPieChart?.zoomOutDisplay === 'assetCount' ||
        (!properties.options?.fieldName && !!properties.options?.colorMapping?.length);

    return (
        <>
            {groups && groups.map((item: any, index: number) => {
                if (showCountWhenZoomedOut && isZoomedOut) {
                    return (
                        <Marker
                            key={index}
                            position={[item.lat, item.lon]}
                            icon={iconCountBadge(item)}
                        />
                    );
                }
                if (properties.options?.fieldName && isZoomedOut && properties.options?.colorMapping) {
                    let color = 'blue';
                    const dominantIndex = item.entries.reduce((acc: number, curr: any) => {
                        const idx = properties.options?.colorMapping?.findIndex(
                            (mapping: any) => mapping.value === curr[properties.options?.fieldName]
                        );
                        return idx >= 0 && idx < acc ? idx : acc;
                    }, properties.options?.colorMapping?.length - 1);
                    color = properties.options?.colorMapping?.[dominantIndex]?.color ?? color;

                    return (
                        <Marker
                            key={index}
                            position={[item.lat, item.lon]}
                            icon={L.divIcon({
                                html: `<svg width="30" height="45" viewBox="0 0 38 55" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19 0C8.507 0 0 8.507 0 19c0 14.25 19 36 19 36s19-21.75 19-36c0-10.493-8.507-19-19-19z" fill="${color}"/>
                                        <circle cx="19" cy="19" r="8" fill="#FFFFFF"/>
                                      </svg>`,
                                className: 'custom-marker',
                                iconSize: [30, 45],
                                iconAnchor: [15, 45],
                                popupAnchor: [0, -45]
                            })}
                        />
                    );
                }
                return (
                    <Marker
                        key={index}
                        position={[item.lat, item.lon]}
                        icon={iconPieChart(item, properties.options || {}, mapContext)}
                    />
                );
            })}
        </>
    )
}

export default React.memo(RenderMarkerSuperCluster)